import { trpc } from "@/util/trpc";
import { usdcContractAddress } from "@talentir/shared/abis/erc-20";
import { tokenContractAddress } from "@talentir/shared/abis/erc-1155";
import { getErrorMessage } from "@talentir/shared/helpers";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useGetLoggedInUser } from "../use-get-logged-in-user";
import { useReservoirkitClient } from "../use-reservoirkit-client";
import { useTalentirChain } from "../use-talentir-chain";
import { useGetReservoirWallet } from "./use-get-reservoir-wallet";
import { findAdjustedUsdcPrice } from "./utils";

export function useCreateListing() {
    const reservoirClient = useReservoirkitClient();
    const reservoir = useGetReservoirWallet();

    const [isLoading, setIsLoading] = useState(false);

    const { user } = useGetLoggedInUser();
    const trpcUtils = trpc.useUtils();
    const reservoirIndex = trpc.lambda.utils.reservoirOrderIndex.useMutation();

    const chain = useTalentirChain();

    const createListing = useCallback(
        async (tokenId: string, quantity: bigint, usdcQuantity: bigint) => {
            if (reservoir == null || chain == null) {
                return;
            }

            // There's a rounding check happening, so we need to calculate the updated USDC quantity
            // We do this silently because it's only a very small difference
            // https://github.com/reservoirprotocol/indexer/blob/0a1bff2bdff780300738938ed3a445ddfd2b9150/packages/sdk/src/seaport-v1.5/order.ts#L190
            const updatedUsdcQuantity = findAdjustedUsdcPrice(usdcQuantity, quantity);

            try {
                setIsLoading(true);
                await reservoirClient?.actions.listToken({
                    listings: [
                        {
                            token: `${tokenContractAddress}:${tokenId}`,
                            weiPrice: updatedUsdcQuantity.toString(),
                            quantity: +quantity.toString(),
                            currency: usdcContractAddress,
                            marketplaceFees: ["0x6480B75A63995ba4748b44A6179fAEC2DcdCf378:250"],
                            options: {
                                "seaport-v1.5": {
                                    // AA users can't cancel off-chain, track progress here:
                                    // https://github.com/reservoirprotocol/indexer/issues/7939
                                    useOffChainCancellation: user?.category !== "magic-with-account-abstraction",
                                },
                            },
                        },
                    ],
                    onProgress: () => {},
                    wallet: reservoir.wallet,
                });

                await reservoirIndex.mutateAsync({ chain });
                await new Promise((resolve) => setTimeout(resolve, 5000));
                await trpcUtils.edge.dashboard.getOpenOrdersV2.invalidate();
                await trpcUtils.edge.dashboard.getSidebarDashboardInfo.invalidate();
            } catch (error) {
                toast.error("Error", { description: getErrorMessage(error) });
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [reservoir, reservoirClient?.actions, trpcUtils, user?.category, chain, reservoirIndex],
    );

    return { createListing, isLoading };
}
